export interface SteamWishlistResponse {
  response: {
    items?: SteamWishlistItem[];
  };
}

export interface SteamWishlistItem {
  appid: number;
  priority: number;
  date_added: number;
}

export interface SteamAppDetailsResponse {
  [appId: string]: {
    success: boolean;
    _is_fallback?: boolean;
    data?: {
      name: string;
      header_image: string;
      is_free?: boolean;
      type: string;
      price_overview?: SteamPriceOverview;
      release_date?: {
        coming_soon: boolean;
        date: string;
      };
      demos?: { appid: number }[];
      genres?: { id: string; description: string }[];
    };
  };
}

export interface SteamPriceOverview {
  currency: string;
  initial: number;      // in cents
  final: number;        // in cents
  discount_percent: number;
  initial_formatted: string;
  final_formatted: string;
}

// ── GOG Types ────────────────────────────────────────────────

export interface GOGResult {
  found: boolean;
  storeLink?: string;
  price?: {
    amount: number;
    baseAmount: number;
    currency: string;
    discount: number;
  };
}

// ── HLTB Types ───────────────────────────────────────────────

export interface HLTBResult {
  id: string;
  name: string;
  imageUrl: string;
  gameplayMain: number;        // seconds
  gameplayMainExtra: number;   // seconds
  gameplayCompletionist: number; // seconds
  similarity: number;
}

// ── App Domain Types ─────────────────────────────────────────

export interface GameEntry {
  appId: string;
  name: string;
  capsuleUrl: string;
  releaseDate: string;
  isComingSoon: boolean;
  isDemo: boolean;
  hasDemo: boolean;
  reviewDesc: string;
  reviewPercent: number;
  reviewCount: number;
  genres: string[];
  isFree: boolean;
  isUnavailable?: boolean;
  priority: number;

  // Price (may be null if free or not fetched)
  priceCurrency: string | null;
  priceInitial: number | null;   // dollars/euros
  priceFinal: number | null;     // dollars/euros
  discountPercent: number;

  // HLTB (may be null if not found)
  hltbId: string | null;
  hltbMain: number | null;       // hours
  hltbMainExtra: number | null;  // hours
  hltbCompletionist: number | null; // hours

  // GOG
  gogUrl: string | null;
  gogPriceCurrency: string | null;
  gogPriceInitial: number | null;
  gogPriceFinal: number | null;
  gogDiscountPercent: number;

  // Status
  hltbStatus: 'pending' | 'found' | 'not_found';
  gogStatus: 'pending' | 'found' | 'not_found';
  priceStatus: 'pending' | 'found' | 'not_found' | 'free' | 'stale' | 'unavailable';
  reviewStatus?: 'pending' | 'loaded' | 'error';
  reviewTimestamp?: number;
  isStale?: boolean;
  dateAdded: number;
}

export interface SteamReviewsResponse {
  success: boolean;
  desc: string;
  percent: number;
  total: number;
}

export interface CachedSteamData {
  name: string;
  capsuleUrl: string;
  priceFinal: number | null;
  priceInitial: number | null;
  discountPercent: number;
  genres: string[];
  isFree?: boolean;
  isComingSoon?: boolean;
  isUnavailable?: boolean;
  isDemo?: boolean;
  hasDemo?: boolean;
  reviewDesc?: string;
  reviewPercent?: number;
  reviewCount?: number;
  reviewTimestamp?: number;
}

export interface SteamCacheEntry {
  data: CachedSteamData;
  timestamp: number;
}

export type SortField =
  | 'name'
  | 'hltbMain'
  | 'hltbMainExtra'
  | 'hltbCompletionist'
  | 'priceFinal'
  | 'discountPercent'
  | 'priority'
  | 'dateAdded';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  field: SortField;
  direction: SortDirection;
}

export interface AppState {
  steamId: string;
  games: GameEntry[];
  sort: SortState;
  filterCategory: string | null;
  loading: boolean;
  loadingMessage: string;
  loadingProgress: number;
  loadingTotal: number;
  error: string | null;
  isCancelled: boolean;
  onStop?: () => void;
  isThrottled: boolean;
  throttledUntil: number | null; // Timestamp
  regionId: string;
  hltbErrorCount: number;
  hiddenGames: Set<string>;
}

export interface SteamRegion {
  id: string;
  name: string;
  cc: string;
  currency: string;
  symbol: string;
}

export const REGION_MAP: Record<string, SteamRegion> = {
  'us': { id: 'us', name: 'United States ($)', cc: 'us', currency: 'USD', symbol: '$' },
  'eu': { id: 'eu', name: 'European Union (€)', cc: 'fr', currency: 'EUR', symbol: '€' },
  'gb': { id: 'gb', name: 'United Kingdom (£)', cc: 'gb', currency: 'GBP', symbol: '£' },
  'ca': { id: 'ca', name: 'Canada ($)', cc: 'ca', currency: 'CAD', symbol: '$' },
  'au': { id: 'au', name: 'Australia ($)', cc: 'au', currency: 'AUD', symbol: '$' },
  'jp': { id: 'jp', name: 'Japan (¥)', cc: 'jp', currency: 'JPY', symbol: '¥' },
};
